import React from 'react';
import Avatar from '../../Avatar/Avatar';
import { H4 } from '../../Heading/Heading';
import IconClock from '../../IconClock/IconClock';
import NamedLink from '../../NamedLink/NamedLink';
import css from './ProductOrderForm.module.css';
import IconLocation from '../../IconLocation/IconLocation';
import IconReviewStar from '../../IconReviewStar/IconReviewStar';
import { FormattedMessage } from '../../../util/reactIntl';
import { PrimaryButtonInline } from '../../Button/Button';
import Spinner from '../../IconSpinner/IconSpinner';
import { getFormattedStoreTime } from '../../../util/dates';
import { getManualStoreRatingData } from '../../../data/manualStoreReviews';
/**
 * A box that displays the provider's information.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.intl - The intl object
 * @param {Object} props.providerUserListing - The provider user listing
 * @param {Object} props.storeTimings - The store timings
 * @param {Object} props.storeTimeZone - The store time zone
 * @param {Object} props.author - The author
 * @param {boolean} props.fetchProviderUserListingInProgress - Whether the provider user listing is being fetched
 */

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const getDayOfWeek = day => {
  return WEEKDAYS.indexOf(day);
};

const formatRating = rating => Number(rating.toFixed(2)).toString();

const combineRatingData = (providerRatingData, manualStoreRatingData) => {
  if (!providerRatingData) {
    return manualStoreRatingData;
  }

  if (!manualStoreRatingData) {
    return providerRatingData;
  }

  const reviewCount = providerRatingData.reviewCount + manualStoreRatingData.reviewCount;
  const ratingTotal =
    providerRatingData.rating * providerRatingData.reviewCount +
    manualStoreRatingData.rating * manualStoreRatingData.reviewCount;

  return {
    rating: ratingTotal / reviewCount,
    reviewCount,
  };
};

const ProviderBox = ({
  providerUserListing,
  storeTimings,
  author,
  fetchProviderUserListingInProgress,
  handleContactUser,
  isOwnListing,
  showContactSeller,
}) => {
  const { aggregateRating, numberOfRatings } = providerUserListing.attributes.publicData || {};
  const providerRatingData =
    aggregateRating && numberOfRatings
      ? {
          rating: aggregateRating / 100,
          reviewCount: numberOfRatings,
        }
      : null;
  const manualStoreRatingData = getManualStoreRatingData(author?.id?.uuid);
  const ratingData = combineRatingData(providerRatingData, manualStoreRatingData);

  const sortedStoreTimings = Object.keys(storeTimings)
    .sort((a, b) => {
      return getDayOfWeek(a) - getDayOfWeek(b);
    })
    .map(day => {
      return {
        day,
        timings: storeTimings[day],
      };
    });

  const { publicData: providerPublicData } = providerUserListing?.attributes || {};
  const { location } = providerPublicData || {};
  const { address, building } = location || {};
  const providerAddress = building ? `${building}, ${address}` : address;

  return (
    <div className={css.providerInfoContainer}>
      {fetchProviderUserListingInProgress ? (
        <Spinner />
      ) : (
        <>
          <div className={css.providerInfo}>
            <Avatar user={author} renderSizes="40px" />
            <NamedLink
              className={css.providerTitleLink}
              name="ProfilePage"
              params={{ id: author.id.uuid }}
            >
              <H4>{providerUserListing.attributes.title}</H4>
            </NamedLink>
          </div>
          <div className={css.providerDetails}>
            {ratingData && (
              <a className={css.ratingLink} href="#reviews">
                <IconReviewStar className={css.starIcon} isFilled />
                <span>
                  {formatRating(ratingData.rating)} ({ratingData.reviewCount} reviews)
                </span>
              </a>
            )}
            <div className={css.flexItem}>
              <IconLocation className={css.icon} />
              <span>{providerAddress}</span>
            </div>

            <div className={css.storeTimings}>
              <div className={css.storeTimingsContent}>
                <IconClock className={css.clockIcon} />
                <div className={css.storeTimingsList}>
                  {sortedStoreTimings.map(({ day, timings }) => (
                    <div className={css.storeTimingItem} key={day}>
                      <div className={css.storeTimingItemDay}>
                        <FormattedMessage id={`EditListingAvailabilityPlanForm.dayOfWeek.${day}`} />
                      </div>
                      <div className={css.storeTimingItemTime}>
                        {getFormattedStoreTime(timings?.startTime)} -{' '}
                        {getFormattedStoreTime(timings?.endTime)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {showContactSeller && (
              <div className={css.contactSellerButton}>
                <PrimaryButtonInline
                  onClick={handleContactUser}
                  type="button"
                  disabled={isOwnListing}
                >
                  <FormattedMessage id="ProductOrderForm.ctaButtonContactSeller" />
                </PrimaryButtonInline>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderBox;
